import { Offer } from "./betData";
import { Sport } from "./language";

/** The top level type that all branding and ad tag data is contained in */
export interface AdTag {
  /** The brand associated with this ad tag */
  brand: Brand;



  /** The ad tag size */
  size: Size;

  /**
   * The gambling operator defined to show first on on the ad tag.
   * This is mostly used for multi operator tags to know which one
   * should be used before the others.
   */
  operator: Operator;

  /** All the operators that have been added to the ad tag */
  operators: Operator[];

  /**
   * The tracking for each operator keyed by their MongoDB ID.
   * The keys need to match the _id field in operator and operators.
   **/
  tracking: Record<string, Tracking>;

  /**
   * Macros is an array of marcros deinfed in the ad tag that
   * in some cases needs to be applied to the tracking
   */
  macros: Macro[];

  /**
   * The team shows the adtag team name and the feature flags related to it.
   */
  team?: AdTagTeam | string;

  /**
   * Primary and secondary behaviours related to the adtag.
   */
  behaviours?: AdTagBehaviours;

  /**
   * Campaigns related to the adtag.
   */
  campaigns?: AdTagCampaign[];

  /**
   * The classifcation of page the ad tag page
   */
  classification?: AdTagClassification;

  /**
   * Experiments related to the adtag.
   */
  experiments?: AdTagExperiment[];

  /** Ad tag identifier */
  _id?: string;
}

/**
 * Primary and secondary behaviours related to the adtag.
 */
export interface AdTagBehaviours {
  primary: AdTagBehavirourType;
  secondary: AdTagBehavirourType;
}

export interface AdTagBehavirourType {
  type: "" | "contextual" | "productSet";
  productSets: {
    [key: string]: {
      productSetRef: {
        productSetId: string;
        // example: SMARTADS
        sortedBy?: string;
      };
    };
  };
}

/**
 * Campaign related to the adtag.
 */
export interface AdTagCampaign {
  /** Campaign identifier */
  _id: string;

  /**
   * Information about the content the campaign is showing
   */
  campaignContent: {
    /**
     * The campaign content type, E.G. what kind of content is the campaign set up to display
     */
    type: "" | "contextual" | "productSet" | "offer-only";

    /**
     * If the campaign is using a product set this is where the product set id will be set
     */
    productSetId: string;

    /**
     * If the campaign is using an offer this where the offer will be set
     */
    offer?: Offer;

    /**
     * If the campaign is set to use a preroll this is where the preroll data will be set
     */
    preroll?: CampaignAdTemplatePreroll;
  };

  /**
   * The target URL is an override URL that can be defined, this URL
   * is used to override any deeplinking in the ads.
   */
  targetUrl?: string;

  /**
   * The targetting that the campaign is using, this must match
   * the pages classification or the campaign shouldn't be applied
   */
  targeting?: {
    /**
     * Sport based targetting
     */
    sport?: {
      /** The name of the player that the campaign targets */
      player: string;

      /** ??? */
      type: string;

      /** The competitions of the player that the campaign targets */
      competition: string[];

      /** The competitors of the player that the campaign targets */
      competitor: string[];
    }[];

    /**
     * Marcos the campaign targets, the campaign
     * should only apply if the ad tag has these macros.
     **/
    macros?: {
      key: string;
      value: string;
    }[];
  };

  /**
   * The operator object defined on the campaign from the Console
   */
  operator: Operator;
}

export interface AdTagTeamFeatures {
  relevancy: boolean;
  smartAds: boolean;
  pixelReimpression?: boolean;
  disableLegacyAnalytics?: boolean;
}

export interface AdTagTeam {
  features: AdTagTeamFeatures;
  name: string;
  _id: string;
}

export interface AdTagClassification {
  sport?: string;
  competitors?: string[] | null;
  competitions?: string[] | null;
}

export interface Size {
  /** Is the ad size responsive */
  responsive: boolean;

  /**
   * The width of the ad, this can be ignored if responsive=true
   * as it should be 100% of the container
   */
  width: number;

  /** The height of the ad */
  height: number;

  /** The name of the ad defined in the Console */
  name: string;

  /**
   * The MongoDB ID, this is optinal because the same type is used when
   * defining a brand in the Console and it won't have an id at that point
   */
  _id?: string;
}

export interface InSightTesting {
  insightRandomTesting: boolean;
}

/**
 * The tracking for each operator
 * */
export interface Tracking {
  /** The affiliate code that should be added onto links */
  affiliateCode: string;

  /**
   * The behaviour for the click through tracking and be fired in the background
   * when the ad loads or it can be added to the click throught tracking as a redirect.
   */
  clickThroughBehaviour: "background" | "redirect";

  /**
   * The URL to be used for click tracking that should be appended to links.
   * This can be an empty string.
   */
  clickThroughUrl: string;

  /**
   * The URL to be used for firing pixels on impression.
   * This can be an empty string.
   */
  impressionUrl: string;

  /**
   * Pixel type defines if the pixel should be loaded using an image tag or a iframe.
   */
  pixelType?: "image" | "iframe";

  /**
   * The target URL is an override URL that can be defined on the ad tag, this URL
   * is used to override any deeplinking in the ads.
   */
  targetUrl: string;

  /**
   * The target url type defines if the ads should use deeplinks or the defined
   * targetUrl address.
   */
  targetUrlType: "deeplink" | "direct";

  /**
   * The tracking marcros uses
   */
  trackingMacros: string[];

  /**
   * Preroll target URL allows you to define a on the ad tag that the
   * preroll will use when clicked by a user.
   */
  prerollTargetUrl: string;

  /**
   * Fallback url is defined on the ad tag and will be used as the
   * replacement for targetUrl when newAdtagLinks is enabled.
   * This will eventually replace all urls on the brand and and tag.
   */
  fallbackUrl: string;

   /**
    * Used to reimpress the tracking pixel as a way to help
    * our analytics get more accuracy against our clients data
    */
  refireAfter?: number;
}

/**
 * The operator object defined on the ad tag from the Console
 */
export interface Operator {
  /** The MonogoDB ID */
  _id?: string;

  /** The name of the operator, this is user defined. */
  name: string;

  /** The operator key use to query the bet store. */
  key: string;

  /** An array of spots supported by this operator. */
  sports?: Sport[];

  /** An array of lanauges supported by this operator. */
  languages?: string[];

  /**
   * If the ad tag has a contextaul product set, the id will for the
   * product set will be set here
   **/
  contextualProductSetId?: string;
}

export interface Brand {
  /** The MonogoDB ID */
  _id?: string;

  /** The name of the brand defined in Console */
  name: string;

  /**
   * The script tag to inject into the iframe if useCustomScript=true, this will
   * replace the normal vue entry point. Should default to an empty string.
   */
  script: string;

  /**
   * Use custom script controls if the script string should be injected into
   * the ad tag iframe instead of the Vue entry point for the ads. You can use this to
   * build your own ads.
   */
  useCustomScript: boolean;

  /** The date the brand was last updated in ISODate format */
  lastUpdated: string;

  /** The date the brand was created in ISODate format */
  createdAt: string;

  /** The MongoID of the team this brand belongs to */
  team?: string;

  /** An array of operator MongoDB ID's that have been added to the ad tag */
  operators: Operator[];

  /** The logos for each opertor keyed by the MongoDB ID's */
  operatorLogos?: Record<string, OperatorLogoPalette>;

  /** A map of colours defined on this brand keyed by ID */
  colors: NormalizedEntity<Color>;

  /** A map of fonts defined on this brand keyed by ID */
  fonts: NormalizedEntity<Font>;

  /** A map of buttons defined on this brand keyed by ID */
  buttons: NormalizedEntity<Button>;

  /** A map of logos defined on this brand keyed by ID */
  logos?: NormalizedEntity<Logo>;

  /** An object containin the users defiend options for the ad */
  adTemplate: AdTemplate;

  /**
   * A map of offers keyed by the operator ID, each operator can
   * have it's own offer
   */
  offers?: Record<string, Offer>;

  /** The type of brand this is, should default to publisher */
  type?: "social" | "publisher";

  /** A map of backgrounds added to this brand keyed by sport or 'default' for all sports. */
  backgroundImageSets?: BackgroundImageSetMap;

  /** A map of prerolls added to this brand keyed by sport or 'default' for all sports. */
  prerollImageSets?: BackgroundImageSetMap;

  /**
   * For type: 'social' ads only.
   * Defines if the ad should only display the event name
   */
  displayEventOnly?: boolean;

  /** show navigation buttons on mobile view */
  showMobileNav?: boolean;
}

/**
 * A utility type for the background image set map, these should be keyed
 * by sport or 'default' for all sports.
 */
export type BackgroundImageSetMap = Record<string, MongoId<BackgroundImageSet>>;

/** The background image set cotains the images for each sport */
export interface BackgroundImageSet {
  /** The MondoDB ID */
  _id?: string;

  /** The name of the background image set */
  name: string;

  /** User assets contains each image in the set */
  userAssets: UserAsset[];
}

/**
 * The user asset model, this is also used in the Console.
 * Note: some of these fields are legacy and aren't used for
 * the ads.
 */
export interface UserAsset {
  /** The file name of the user assets uploaded */
  name: string;

  /** The type of asset it's being used for */
  assetType: "background" | "preroll";

  /** The address to the uploaded image on the CDN */
  binary: string;

  /** The size the asset has been assigned to, this will be a MongoDB ID */
  adSlotSize?: string;

  /** The MongoDB ID */
  _id?: string;

  /** The team the user asest belongs to */
  team?: string;

  /** The MongoDB document version number */
  __v?: number;

  /** Has the user asset been deleted */
  archived?: boolean;

  /** The date the brand was last updated in ISODate format */
  createdAt?: string;

  /** The date the brand was created in ISODate format */
  lastUpdated?: string;

  /**
   * A legcy value that could contain different media types, this will
   * always be 'image' now
   */
  binaryType?: "image";

  /**
   * A legcy value that allowed you to assign assetes to an operator.
   * This field isn't used anymore.
   */
  operator?: string;

  /**
   * A legcy value that allow you to assign assets to an sport.
   * This field isn't used anymore.
   */
  sport?: string;
}

/**
 * A utility type for using a map with a generic type
 */
export interface NormalizedEntity<T> {
  [x: string]: T;
}

/** A color for use in a color palette */
export interface Color {
  /* Color is the CSS value for a color */
  color: CSSStyleDeclaration["color"];

  /** The name of the color defined by the user */
  name: string;
}

/** A font for use in a font palette */
export interface Font {
  /** The name of the font defined by the user */
  name: string;

  /* The color ID of the font, you can look
   * this up in the colors map.
   */
  color: string;

  /* Text transform is the CSS value for text-transform */
  textTransform?: TextTransformType;

  /* Text decoration is the CSS value for text-decoration */
  textDecoration?: TextDecorationType;

  /* Font style is the CSS value for font-style */
  fontStyle?: FontStyleType;

  /* Font shadow is the CSS value for text-shadow */
  textShadow?: string;

  /* Font asset is the details for the font */
  fontAsset: FontAsset;
}

/** Values for the font style */
export type FontStyleType = "italic" | "unset";

/** Values for the text decoration */
export type TextDecorationType = "underline" | "none";

/** The value for the text transform */
export type TextTransformType =
  | "uppercase"
  | "lowercase"
  | "capitalize"
  | "none";

/** Type Alias for use with mapping the logo */
export type Logo = UserAsset;

/** The font assets incldues the details for loading the font into the browser */
export interface FontAsset {
  /** Family is the name of font family this font belongs to */
  family: string;

  /** The font family is the CSS value for the front-family rule */
  fontFamily: string;

  /** The name of the font */
  name: string;

  /** The URL for the font to be loaded from use the CDN */
  url: string;

  /**
   * @deprecated This was meant to be used to scale fonts line hights
   * based on the size font. It was never implemented into the ads.
   **/
  lineHeightScalar: number;

  /**
   * @deprecated This was meant to be used to normalise fonts size.
   * It was never implemented into the ads.
   **/
  sizeScalar: number;
}

/** The values for the logo palette map */
export interface OperatorLogoPalette {
  /** The logo for desktop ads (this is used for all ads, mobile and desktop) */
  desktop?: Logo;

  /**
   * The color ID for the background color of the logo, you
   * can look this up in the colors map.
   */
  backgroundColor?: string;
}

/**
 * Button states defines all the colors in a button for
 * the different pseudo states
 */
export interface ButtonState {
  /**
   * The ID to use for the primary button color, you
   * can look this up in the colors map
   */
  primaryColor?: string;

  /**
   * The ID to use for the secondary button color, you
   * can look this up in the colors map
   */
  secondaryColor?: string;

  /**
   * The ID to use for the button background color, you
   * can look this up in the colors map
   */
  backgroundColor?: string;

  /**
   * The ID to use for the border color color, you
   * can look this up in the colors map
   */
  borderColor?: string;
}

/** The values for the types of buttons available */
export type buttonType = "odds" | "carouselNav" | "cta" | "parlay";

/** A button contains all the values a user can change to style a button */
export interface Button {
  /** The name of the button */
  name: string;

  /** The type of button, this defines what it can be used for */
  type: buttonType;

  /**
   * The ID to use for the primary font, you
   * can look this up in the fonts map.
   */
  primaryFont: string;

  /**
   * The ID to use for the secondary font, you
   * can look this up in the fonts map.
   */
  secondaryFont: string;

  /**
   * The ID to use for the background color, you
   * can look this up in the colors map.
   */
  backgroundColor?: string;

  /**
   * The border radius number to be used to set the
   * butttons border-radius CSS value.
   */
  borderRadius?: number;

  /**
   * The ID to use for the border color, you
   * can look this up in the colors map.
   */
  borderColor?: string;

  /**
   * The CSS value for the border style
   */
  borderStyle?: CSSStyleDeclaration["borderStyle"];

  /**
   * The CSS value for the border width
   */
  borderWidth?: CSSStyleDeclaration["borderWidth"] | number;

  /**
   * Enabling auto interactions will auto styles the hover
   * and click colors based colors set. True is the only option
   * currently supported.
   * @default true
   */
  autoInteractions: boolean;

  /**
   * The ID to use for the icon color, you
   * can look this up in the colors map.
   */
  iconColor: string;

  /** The various button state styling options */
  actions: {
    /** The button styling for the hover state */
    hover?: ButtonState;

    /** The button styling for the active state */
    active?: ButtonState;

    /** The button styling for the selected state */
    selected?: ButtonState;
  };

  /** entry and repeating animation names for when the button appears on view point */
  animations: {
    //eslint-disable-next-line
    entry: `${EntryButtonAnimationEnum}`
    repeat:`${RepeatButtonAnimationEnum}`
  };
}

export enum EntryButtonAnimationEnum {
  BOUNCE_IN = "bounceIn",
  FLIP_X = "flipX",
  NONE = "none"
}
export enum RepeatButtonAnimationEnum {
  BOUNCE = "bounce",
  PULSE = "pulse",
  RUBBER_BAND = "rubberBand",
  HEAD_SHAKE = "headShake",
  HEART_BEAT = "heartBeat",
  NONE = "none"
}

/** The ad template options */
export interface AdTemplate {
  /**
   * The ID of the ad background color, you
   * can look this up in the colors map.
   */
  backgroundColor: string;

  /**
   * The ID of the ad's inner card background color.
   * By default it's the same as the background.
   */
  cardBackgroundColor: string;

  /** The ad card border radius */
  cardBorderRadius: number;

  /** The ad border radius */
  borderRadius: number;

  /**
   * The ID of the market border color, you
   * can look this up in the colors map.
   */
  marketBorderColor: string;

  /**
   * The ID of the carousel navigation button, you
   * can look this up in the buttonss map.
   */
  carouselNavigationButton: string;

  /**
   * Enable background click allows you to make
   * the background of the ad into a link.
   */
  enableBackgroundClick?: boolean;

  /** Styling options for the team branding */
  team: AdTemplateTeam;

  /** Styling options for the market insites */
  insights?: AdTemplateInsight;

  /** Styling options for the ad headers */
  header: AdTemplateHeader;

  /** Styling options for the market cards */
  market: AdTemplateMarket;

  eventTag: AdTemplateEventTag;

  /** Styling options for the ad footers */
  footer: AdTemplateFooter;

  /** Styling options for the ad offer card */
  offer?: AdTemplateOffer;


  casino?: AdTemplateCasino

  /**
   * Enable/disable if the offer card should be
   * displayed as a fallback when there is no bet data
   * available.
   */
  displayOffersAsFallback: boolean;

  /**
   * Styling options for the ad jackpot. This is only used
   * with Brand.type: 'social'
   */
  jackpot?: AdTemplateJackpot;

  /** Styling options for potential winnings */
  potentialWinnings?: PotentialWinnings;

  /** Styling options for prerolls */
  preroll?: AdTemplatePreroll;

  /** Styling options for the parlay market card */
  parlayCard?: ParlayCard;


  cardOrdering?: CardOrdering

  /**
   * @deprected Use `Adtemplate.CardOrdering` instead
   *
   * Controls the order in which the cards are displayed in the ads
   *
  */

  eventCardOrder?: CardTypes[];

  /** Styling options for head to head card */
  h2hCard?: H2hCard;

  /** the count of promos */
  promoCount?: number

  /**
   * Use the competition name instead of sport name.
   */
   useCompetitionName?: boolean;

  eventOnly?: AdTemplateEventOnly
}

export type AdTemplateElementPosition = {
  x: number;
  y: number;
};

export type AdTemplateElementDimensions = {
  width: number;
  height: number;
};

export type AdTemplateEventOnly = {
  positions: AdTemplateElementPosition;
  alignment: "flex-start" | "center" | "flex-end"
  dimensions: AdTemplateElementDimensions;
};
export interface CardOrdering {
  general: Array<Extract<CardTypes, 'offer' | 'parlay'> | 'events' | 'casino'>
  event: Array<Exclude<CardTypes, 'offer' | 'parlay' | 'casinoHighlight' | 'casinoList' >>
}




export interface AdTemplateCasino {
  /**
   *  button id for casino card cta buttons
   * */
  button : string

  /**
   * style fields for highlight card specific elements
   */
  highlight: {
    /**
    * font to be used for casino highlight text
    */
    font: string
  }
  /**
  * style fields for list card specific elements
  */
  list: {

  /**
   *  font to be used for casino list items text
   */
    font: string

    /**
     * fields for casino-list-header text
     */
    header: {
      /**
       * text to be used in the casino list header
       */
      text:string
      /**
       * font to be used for casino list header
       */
      font: string
    }
  }
}


/**
 * Styling options for the parlay market card What tells the if we're
 * going to use parlay cards (old useParlayCard) is if we have that card on the
 * adTemplate (EX: adTemplate.cardOrdering.general.includes('parlay'))
 **/
export interface ParlayCard {
  /**
   * The ID to use for the stake font, you
   * can look this up in the fonts map.
   */
  stakeFont: string;

  /**
   * The ID to use for the team text button, you
   * can look this up in the buttons map.
   */
  teamTextButton: string;

  /**
   * The ID to use for theempty picks button, you
   * can look this up in the buttons map.
   */
  emptyPicksButton: string;

  /**
   * Custom card title that will show in the header instead
   * of the word "Parlay"
   */
  cardTitle?: string;
}

/**
 * Styling options for the head to head market card. What tells the if we're
 * going to use h2h cards (old useH2hCard) is if we have that card on the
 * adTemplate (EX: adTemplate.cardOrdering.event.includes('h2h'))
 * */
export interface H2hCard {
  /** Win slide color */
  winColor: string;

  /** Draw slide color */
  drawColor: string;

  /** Loss slide color */
  lossColor: string;

  /** Enable/disable team colors */
  useTeamColors: boolean;

  /** User defined home team color */
  homeTeamColor: string;

  /** User defined away team color */
  awayTeamColor: string;

  /** border color for the match history */
  historyBorderColor: string;

  /** text color for the match history */
  historyTextColor: string;

  /** stat slider divider circle color */
  statisticDividerColor: string;

  /** border color for the stat slider */
  statisticBorderColor: string;

  /** font for the statistic value */
  statisticValueFont: string;

  /** font for statistic text */
  statisticTextFont: string;
}

/**
 * The supported leagues for the insite card, these are the only
 * markets that insites will be displayed
 */
export type InsightLeagueArray = Array<
  "NBA" | "MLB" | "NFL" | "NHL"
>;
export type InsightSportsArray = Array<"Football" | "American Footbal" | "Baseball" | "Ice Hockey">;

/** Styling options for the market insites */
export interface AdTemplateInsight {
  /** The enabled leagues for the insite card */
  enabledLeagues: InsightLeagueArray
  enabledSports?: InsightSportsArray
  enabledAbTesting?: boolean;
}

/** Styling options for prerolls */
export interface AdTemplatePreroll {
  /** Enable/disable the prerolls */
  usePreroll?: boolean;

  /** The URL to direct users to who click on the preroll to */
  prerollTargetUrl?: string;

  /** The duration to show the preroll for. 0 == infinite */
  prerollDuration?: number;
}

export interface CampaignAdTemplatePreroll extends AdTemplatePreroll {
  /** The imageSet used for campaigns */
  prerollImageSet?: MongoId<BackgroundImageSet>;
}

/**
 * Styling options for potential winnings
 * @todo Add this boolean for easier check if the potential
 * winnings is selected showPotentialWinnings: boolean;
 */
export interface PotentialWinnings {
  /** The stake amount to use when calculating the winnings */
  stake: number;

  /** The currency to display the potential winnings in */
  currency: string;

  /**
   * The ID to use for the stake button, you
   * can look this up in the buttons map.
   */
  stakeButton?: string;

  /**
   * The ID to use for the stake font, you
   * can look this up in the fonts map.
   */
  stakeFont: string;

  /**
   * The ID to use for the stake amount font, you
   * can look this up in the fonts map.
   */
  stakeAmountFont: string;
}

/** Styling options for the team branding */
export interface AdTemplateTeam {
  /** Enable/disabled the team branding */
  useTeamBranding: boolean;

  /** Enable/disabled team colours */
  useTeamColors: boolean;

  /** Enable/disabled additional branding options */
  useAdditionalBrandingOptions?: boolean;

  /** Prioritise the use custom assets (if available) over team jerseys */
  useCustomAssets?: boolean;

  /** Enable/disabled team badges */
  useTeamBadges: boolean;

  /** Enable/disabled team jerseys */
  useTeamJerseys?: boolean;

  /** Team banner styling options */
  banner: {
    /**
     * The ID to use for the background color, you
     * can look this up in the colors map.
     */
    backgroundColor: string;

    /**
     * The ID to use for the banner font, you
     * can look this up in the fonts map.
     */
    font: string;
  };
  /** Versus icon styling options */
  versusIcon: {
    /**
     * The ID to use for the background color, you
     * can look this up in the colors map.
     */
    backgroundColor: string;

    /**
     * The ID to use for the border color, you
     * can look this up in the colors map.
     */
    borderColor: string;

    /**
     * The ID to use for the banner font, you
     * can look this up in the fonts map.
     */
    font: string;
  };
  /** Badge styling options */
  badge: {
    /**
     * The ID to use for the border color, you
     * can look this up in the colors map.
     */
    borderColor: string;

    /**
     * Filters that allow you to control and which operators, sports
     * and cometitons you want to display badges for.
     */
    filters: BadgeFilters[];

    /**
     * Leagues where the operator has partnership with.
     * When selected, the league logo will appear next to the operator logo and
     * team IP is used where available.
     */
    leagueLogos?: Omit<BadgeFilters, 'sports'>[];
  };
}

/** Styling options for the ad offer card */
export interface AdTemplateOffer {
  /**
   * The ID to use for the header font, you
   * can look this up in the fonts map.
   */
  header: string;

  /**
   * The ID to use for the body font, you
   * can look this up in the fonts map.
   */
  body: string;

  /**
   * The ID to use for the button font, you
   * can look this up in the fonts map.
   */
  button: string;

  /**
   * The ID to use for the terms font, you
   * can look this up in the fonts map.
   */
  terms: string;
}

/** Badge filters options */
export interface BadgeFilters {
  /** The operator the filter is for, this is a MongoDB */
  operator: string;

  /** The sports this filter applies to */
  sports: Sport[];

  /** The competitions this filter applies to */
  competitions: string[];
}

/** Styling options for the ad headers */
export interface AdTemplateHeader {
  /** Styling options for the ad headers first line of text */
  lineOne: Text;

  /** Styling options for the ad headers second line of text */
  lineTwo: Text;

  /** The type header the ads should use */
  type: headerType;

  /** Styling for the CTA button, only used if header.type === 'cta' */
  ctaButton: AdTemplateHeaderCtaButton;
}

/** Styling options for the CTA button */
export interface AdTemplateHeaderCtaButton {
  /**
   * The ID to use for the buttons, you
   * can look this up in the buttons map.
   */
  id: string;

  /** The text to display in the button */
  text: string;

  /**
   * URL defines where the CTA button should link
   * to when a view of an ad clicks on it
   */
  url: string;
}

/** The types of ad header available */
export type headerType = "offer" | "cta";

/** The types of odds that the ads can display */
export enum oddsTypeEnum {
  /** @example 1,4 */
  EUROPEAN_DECIMAL_ODDS = "europeanDecimalOdds",
  /** @example +100 */
  AMERICAN_ODDS = "americanOdds",
  /** @example 1.4 */
  DECIMAL_ODDS = "decimalOdds",
  /** @example 3/5 */
  FRACTIONAL_ODDS = "fractionalOdds",
  /** @example 20% */
  IMPLIED_PROBABILITY = "impliedProbability",
  /** @example £100 */
  POTENTIAL_WINNINGS = "potentialWinnings"
}

/** Utility type for the types of odds that the ads can display */
export type oddsType =
  | oddsTypeEnum.EUROPEAN_DECIMAL_ODDS
  | oddsTypeEnum.AMERICAN_ODDS
  | oddsTypeEnum.DECIMAL_ODDS
  | oddsTypeEnum.FRACTIONAL_ODDS
  | oddsTypeEnum.IMPLIED_PROBABILITY
  | oddsTypeEnum.POTENTIAL_WINNINGS;

/** Styling options for the market cards */
export interface AdTemplateMarket {
  /**
   * The ID to use for the market name font, you
   * can look this up in the fonts map.
   */
  marketNameFont: string;

  /**
   * The ID to use for the odds button, you
   * can look this up in the fonts map.
   */
  oddsButton: string;


  /**
   * The ID to use for the casino button, you
   * can look this up in the fonts map.
   *
   * @deprecated use adtemplate.casino.button
   */
  casinoCTAButton: string;

  /**
   * The odds button template changes how the
   * ads display the odds
   */
  oddsButtonTemplate: oddsType;

  /**
   * The ID to use for the fixture time font, you
   * can look this up in the fonts map.
   */
  fixtureTimeFont: string;

  /**
   * The ID to use for the fixture sport font, you
   * can look this up in the fonts map.
   */
  fixtureSportFont: string;

  /**
   * The ID to use for the team name font, you
   * can look this up in the fonts map.
   */
  teamNameFont: string;

  /**
   * The ID to use for the promo text font, you
   * can look this up in the fonts map.
   */
  promoTextFont: string;

  /**
   * The ID to use for the promo odds font, you
   * can look this up in the fonts map.
   */
  promoOddsFont?: string;


  /**
   * the ID to use for the Casino card CTA text
   * @deprecated use adtemplate.casino.highlight.font
   */
  casinoCTATextFont?: string
  /**
   * the ID to use for the Casino List card text
   * @deprecated use adtemplate.casino.list.font
   */
  casinoListTextFont?: string
  /**
   * the ID to use for the Casino List card header text
   * @deprecated use adtemplate.casino.list.header.font
   */
  casinoListHeaderFont?: string
}

export interface AdTemplateEventTag {
  mainTextFont: string;
  subTextFont: string;
}

/** Styling options for the ad footers */
export interface AdTemplateFooter extends Partial<Text> {
  /**
   * Use scrolling text allows you to fit more terms into the
   * add by make them scrol.
   */
  useScrollingText: boolean;

  /**
   * The amount of time is should take for the scrolling
   * temrs to fully anaimate across the ad footer
   */
  animationTime?: number;

  /** The none animated terms text */
  static: Text;

  /** The scrolling terms text */
  scrolling?: Text;
}

/**
 * Styling options for the ad jackpot. This is only used
 * with Brand.type: 'social'
 */
export interface AdTemplateJackpot {
  /**
   * The ID to use for the font, you
   * can look this up in the font map.
   */
  font: string;

  /**
   * Top is used to offset the jackpot from
   * the top of the ad in pixels.
   */
  top: CSSStyleDeclaration["top"];

  /** The CSS text-align for the jackpot */
  textAlign: CSSStyleDeclaration["textAlign"];

  /**
   * The max height for the container
   * holding the jackpot value
   */
  maxHeight: number;
}

/** Text styling options */
export interface Text {
  /** The text value */
  value: string;

  /**
   * The ID to use for the font, you
   * can look this up in the font map.
   */
  font: string;
}

/**
 * Ad form is the data we bubble up on a event in the preview
 * web component when you hover over the different elements.
 */
export type AdForm = {
  /** The HTML element that is being interacted with */
  el: EventTarget & HTMLDivElement;

  /** The type of inputs the element supports */
  inputs: AdFormInput<AdFormInputTypes>[];

  /** The title of the element */
  title: string;
};

/** A utility type for the that adds the ad from to a custom event */
export type AdClickEvent = CustomEvent<AdForm>;

/** The different imput types supported by the ad form */
export type AdFormInputTypes =
  | "font"
  | "text"
  | "color"
  | "button"
  | "navigationButton"
  | "ctaButton";

/** A utility type for unpacking the ad form input types */
export type FormValueUnpacker<T extends AdFormInputTypes> = T extends "font"
  ? Font
  : T extends "text"
  ? Text
  : T extends "color"
  ? Color
  : T extends "button"
  ? Button
  : T extends "navigationButton"
  ? Button
  : T extends "ctaButton"
  ? Button
  : never;

/**
 * ad form input contains the setter and getter
 * for updating and reading the input values.
 */
export type AdFormInput<T extends AdFormInputTypes> = {
  /** The input type */
  type: T;

  /** Allows to setting the value, this can be directly mutated */
  set: (value: string) => void;

  /** The current value of the input */
  get: () => string;

  /** The name of the input */
  name?: string;
};

export type FilteredKeyOf<T, TK> = keyof Pick<
  T,
  { [K in keyof T]: T[K] extends TK ? K : never }[keyof T]
>;

/** The information about the ad size */
export type TemplateSize = {
  /** Is the ad size responsive */
  responsive: boolean;

  /** The width of the ad */
  width: number;

  /** The hight of the ad */
  height: number;

  /** the name of the ad size */
  name: string;
};

/** A single macro that has been added to the ad tag */
export interface Macro {
  query: MacroQuery[];
}

/** A macro query that has been added to the ad tag */
export interface MacroQuery {
  /** The key of the URL query string */
  key: string;

  /** The value of a URL query string */
  value: string;

  /**
   * @deprecated This doesn't seem to be used by the ads
   * The type of query string
   * */
  type: string;

  /**
   * If track is enabled then the query string will
   * be also added to the tracking pixel
   */
  track: boolean;
}

/** The types of market cards available in the ads */
export type CardTypes =
  | "promo"
  | "mainMarket"
  | "insight"
  | "wdw"
  | "ww"
  | "list"
  | "offer"
  | "parlay"
  | "h2h"
  | "casinoHighlight"
  | "casinoList"

/**
 * A utiliy type for using MongoDB ID's that
 * could also be populated
 */
export type MongoId<T> = T | null | string;

/**
 * AdTag experiment object (small object sent by the ad-server).
 * It has the variants related to the adtag.
 */
export interface AdTagExperiment {
  _id: string;
  type: ExperimentType;
  variants: AdTagExperimentVariant[];
}

export type ExperimentType = 'offer' | 'cardOrdering'

export type ExperimentTracking = {
     impressionUrl: string
}
export interface AdTagExperimentVariant {
  id: string;
  operators: {
    [key: string]: {
      offer: Offer;
      cardOrdering: CardOrdering
      brand?: null | string;
      productSet?: null | string;
      operator: string;
      type: "control" | "variantB";
      tracking: ExperimentTracking
    };
  };
}

export interface UrlQuery {
  ref?: string;
  sport?: string;
  competitor?: string;
  competition?: string;
  useMockData?: string;
  [x: string]: string | undefined;
}




export interface CasinoResponse {
  amount_of_games: number,
  games: Array<CasinoGame>
}

export interface CasinoGame {
  casino_game_operator: string
  casino_game_name:  string
  casino_game_image_url: string
  casino_game_segment:  string
  casino_game_call_to_action:  string
  casino_game_order: number
}


export type CasinoDataSuccess = {
  data: {
    highlight: CasinoGame;
    list: CasinoResponse["games"];
    raw: CasinoResponse;
  };
  status: "success";
};

export type CasinoDataPending = {
  data: null;
  status: "pending";
};

export type CasinoDataError = {
  data: string;
  status: "error";
};

export type CasinoData =
  | CasinoDataSuccess
  | CasinoDataPending
  | CasinoDataError;

  export interface AnalyticsTracking {
    pixels: PixelTracking
  }
  export interface PixelTracking {
      clickThroughBehaviour: "background" | "redirect";
      clickThroughUrl: string;
      impressionUrl: string;
      pixelType: "image" | "iframe";
  }
